/**
 * DashboardPage — Dashboard tab home screen showing financial health
 *
 * Layout (post-audit):
 *   Hero header (teal gradient) → burn amount + sparkline integrated
 *   Review nudge (dashed orange, threshold >= 1)
 *   Activity zones (in-place accordion)
 *   Accounts accordion (Checking / Savings / Credit Cards)
 *   SyncRow (below accounts card)
 *   FAB
 *
 * Mounted at /app/dashboard route.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { categorizeTransaction } from '@/services/categorizeTransaction';
import { useAuth } from '@/contexts/AuthContext';
import { useStableProfile } from '@/hooks/useStableProfile';
import { useBurnProgress } from '@/hooks/useBurnProgress';
import { useMonthlyFinancials } from '@/hooks/useMonthlyFinancials';
import { useArtTransactions } from '@/hooks/useArtTransactions';
import { useVerdicts } from '@/hooks/useVerdicts';
import { useSubscription } from '@/hooks/useSubscription';
import { ActivityCard } from '@/components/money/ActivityCard';
import { DashboardHero } from '@/components/money/DashboardHero';
import type { HeroMode } from '@/components/money/DashboardHero';
import { NetSummaryCard } from '@/components/money/NetSummaryCard';
import { InlineReviewSection } from '@/components/money/InlineReviewSection';
import { TransactionDetailSheet } from '@/components/money/TransactionDetailSheet';
import { CategorizationSheet } from '@/components/money/CategorizationSheet';
import { IncomeLinkingSheet } from '@/components/money/IncomeLinkingSheet';
import { MatchArtworkBottomSheet } from '@/components/money/MatchArtworkBottomSheet';
import { StatExplainerSheet } from '@/components/money/StatExplainerSheet';
import { useMatchSuggestions } from '@/hooks/useMatchSuggestions';
import { AccountsAccordion } from '@/components/money/AccountsAccordion';
import { CombinedAccountsWidget } from '@/components/money/CombinedAccountsWidget';
import { UpgradeSheet } from '@/components/subscription/UpgradeSheet';
import { CompleteSetupChecklist } from '@/components/money/CompleteSetupChecklist';
import { InsightCard } from '@/components/money/InsightCard';
import { FeedbackRow } from '@/components/money/FeedbackRow';
import { SyncRow } from '@/components/money/SyncRow';
import { usePlaidConnectionStatus } from '@/hooks/usePlaidConnectionStatus';
import { usePlaidAccounts } from '@/hooks/usePlaidAccounts';
import { usePlaidConnection } from '@/hooks/usePlaidLink';
import { EXPLAINERS } from '@/copy/explainers';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import type { ArtTransaction } from '@/hooks/useArtTransactions';
import { T } from '@/styles/tokens';
import { DashboardSkeleton } from '@/components/ui/SkeletonScreen';

// ── Styles ──

const PAGE_STYLES = `
.dash-app {
  font-family: ${T.fontFamily};
  background: ${T.cream};
  color: ${T.text};
  min-height: 100vh;
  max-width: 420px;
  margin: 0 auto;
  position: relative;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}
@keyframes dashFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
.dash-enter {
  animation: dashFadeIn 0.28s ${T.easeEntrance} both;
}
.dash-stagger-1 { animation-delay: 0ms; }
.dash-stagger-2 { animation-delay: 80ms; }
.dash-stagger-3 { animation-delay: 160ms; }
.dash-stagger-4 { animation-delay: 240ms; }
@keyframes fabSlideIn {
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes zoneExpand {
  from { opacity: 0; max-height: 0; }
  to { opacity: 1; max-height: 400px; }
}
@keyframes greetFadeIn {
  0% { opacity: 0; transform: translateY(4px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes greetCrossfade {
  0%, 70% { opacity: 1; }
  85% { opacity: 0; transform: translateY(-2px); }
  86% { opacity: 0; transform: translateY(2px); }
  100% { opacity: 1; transform: translateY(0); }
}
`;

// HeroSparkChart extracted to @/components/money/HeroSparkChart

// ── Main Dashboard ──

export function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { profile, isFirstLoad: profileFirstLoad } = useStableProfile();

  // Data hooks
  const burnProgress = useBurnProgress();
  const { verdicts, markVerdictSold, isLoading: verdictsLoading } = useVerdicts();
  const financials = useMonthlyFinancials(verdicts);
  const { transactions, uncertainTransactions, uncertainCount } = useArtTransactions();
  const { suggestions: matchSuggestions, acceptSuggestion, dismissSuggestion } = useMatchSuggestions();
  const { mode: dashboardMode } = useDashboardMode();
  const { isPremium, isTrialing, trialDaysRemaining } = useSubscription();

  // Show skeleton only on genuine cold start (profile never loaded)
  if (profileFirstLoad) return <DashboardSkeleton />;

  // Notification badge count (mirrors NotificationsPage logic)
  const notificationBadgeCount = useMemo(() => {
    // Don't compute until verdicts have loaded — prevents badge flash
    if (verdictsLoading) return 0;
    const dismissed: string[] = (() => { try { return JSON.parse(localStorage.getItem('tp_dismissed_notifications') || '[]'); } catch { return []; } })();
    let count = 0;
    // Setup incomplete
    const setupDone = [
      verdicts.length >= 1,
      verdicts.some(v => v.sold === true),
      isPremium === true,
      profile?.highestSale != null,
      profile?.pdfBrandName != null && profile.pdfBrandName !== '',
    ].filter(Boolean).length;
    if (setupDone < 5 && !dismissed.includes('setup')) count++;
    if (isTrialing && trialDaysRemaining != null && trialDaysRemaining <= 7 && !dismissed.includes('trial')) count++;
    if (isPremium && verdicts.length >= 3 && !dismissed.includes('forensics')) count++;
    // Unreviewed transaction counts — per-count dismissal keys so new txns relight the bell
    const unreviewedSpend = profile?.unreviewedSpendCount ?? 0;
    const unreviewedEarned = profile?.unreviewedEarnedCount ?? 0;
    if (unreviewedSpend > 0 && !dismissed.includes(`unreviewedSpend:${unreviewedSpend}`)) count++;
    if (unreviewedEarned > 0 && !dismissed.includes(`unreviewedEarned:${unreviewedEarned}`)) count++;
    return count;
  }, [verdicts, isPremium, isTrialing, trialDaysRemaining, profile, verdictsLoading]);

  const hasBankConnected = profile?.burnSource === 'plaid';
  // User previously connected a bank but has since disconnected all accounts
  const hadBankEver = profile?.burnSource !== undefined;

  // Plaid connection + accounts
  const { connections, needsReauth, reauthItemId } = usePlaidConnectionStatus(user?.uid);
  const { accounts: plaidAccounts, lastSyncAt: accountsSyncAt } = usePlaidAccounts(user?.uid);
  const { connect: plaidConnect, reconnect: plaidReconnect, connectionResult, connected: plaidJustConnected } = usePlaidConnection();
  const [showConnectionCeremony, setShowConnectionCeremony] = useState(false);

  // Show ceremony popover when Plaid connection completes
  useEffect(() => {
    if (plaidJustConnected && connectionResult && profile?.hasSeenConnectionCeremony !== true) {
      setShowConnectionCeremony(true);
    }
  }, [plaidJustConnected, connectionResult, profile?.hasSeenConnectionCeremony]);


  const dismissCeremony = useCallback(() => {
    setShowConnectionCeremony(false);
    if (user) {
      updateDoc(doc(db, 'users', user.uid), { hasSeenConnectionCeremony: true }).catch(() => {});
    }
  }, [user]);

  // Hero mode (art/survival toggle) — persisted to localStorage
  const [heroMode, setHeroMode] = useState<HeroMode>(() => {
    try { const s = localStorage.getItem('tp-hero-mode'); if (s === 'survival') return 'survival'; } catch {}
    return 'art';
  });
  const handleHeroModeChange = useCallback((mode: HeroMode) => {
    setHeroMode(mode);
    try { localStorage.setItem('tp-hero-mode', mode); } catch {}
  }, []);

  // Sheet state
  const [selectedTxn, setSelectedTxn] = useState<ArtTransaction | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [catSheetOpen, setCatSheetOpen] = useState(false);
  const [linkSheetOpen, setLinkSheetOpen] = useState(false);
  const [matchSheetOpen, setMatchSheetOpen] = useState(false);
  const [matchSheetTxn, setMatchSheetTxn] = useState<{ id: string; amount: number; merchant: string; date: string } | null>(null);
  const [catToast, setCatToast] = useState<string | null>(null);
  const [burnExplainerOpen, setBurnExplainerOpen] = useState(false);
  const [upgradeSheetOpen, setUpgradeSheetOpen] = useState(false);

  // Animate zone amounts after review queue closes
  const [zoneAnimateKey, setZoneAnimateKey] = useState(0);

  // Handle income-linking from drill-down page (via route state)
  useEffect(() => {
    const state = location.state as { linkTxnId?: string } | null;
    if (state?.linkTxnId) {
      const txn = transactions.find((t) => t.id === state.linkTxnId);
      if (txn) {
        const formatted = new Date(txn.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        setMatchSheetTxn({ id: txn.id, amount: txn.amount, merchant: txn.merchantName, date: formatted });
        setMatchSheetOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, transactions, navigate, location.pathname]);

  // Link income transaction to artwork (Flow B)
  const handleLink = useCallback(async (verdictId: string) => {
    if (!selectedTxn || !user) return;
    try {
      await markVerdictSold(verdictId, Math.abs(selectedTxn.amount), undefined, selectedTxn.date, undefined, 'confirmed', selectedTxn.id);
      const txnRef = doc(db, 'users', user.uid, 'transactions', selectedTxn.id);
      await updateDoc(txnRef, { tpLinkedArtworkId: verdictId });
      setLinkSheetOpen(false);
      setTimeout(() => setSelectedTxn(null), 280);
    } catch (err) {
      console.error('[DashboardPage] Link error:', err);
    }
  }, [selectedTxn, user, markVerdictSold]);

  // Inline review categorization (replaces ReviewQueue swipe)
  const handleInlineReviewCategorize = useCallback(async (txn: ArtTransaction, category: string) => {
    if (user) {
      await categorizeTransaction(user.uid, txn.id, category);
    }
    // Show confirmation toast
    const labels: Record<string, string> = { ART_INCOME: 'Income', ART_SUPPLY: 'Spend', PERSONAL: 'Personal', IGNORE: 'Ignored' };
    setCatToast(`Categorized as ${labels[category] || category}`);
    setTimeout(() => setCatToast(null), 2000);
    // If categorized as income, prompt linking via match sheet
    if (category === 'ART_INCOME') {
      const formatted = new Date(txn.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      setMatchSheetTxn({ id: txn.id, amount: txn.amount, merchant: txn.merchantName, date: formatted });
      setMatchSheetOpen(true);
    }
    setZoneAnimateKey((k) => k + 1);
  }, [user]);

  // Link a Pool 2 verdict to a bank transaction
  const handleLinkMatch = useCallback(async (verdictId: string, txnId: string) => {
    if (!user) return;
    try {
      const { linkVerdictToTransaction } = await import('@/services/verdicts');
      await linkVerdictToTransaction(user.uid, verdictId, txnId);
      // Clear the match suggestion
      const suggestion = matchSuggestions.find(s => s.verdictId === verdictId && s.transactionId === txnId);
      if (suggestion) await acceptSuggestion(suggestion.id);
    } catch (err) {
      console.error('[DashboardPage] Link match error:', err);
    }
  }, [user, matchSuggestions, acceptSuggestion]);

  // Recategorize from detail sheet
  const handleDetailRecategorize = useCallback(async (txn: ArtTransaction, category: string) => {
    if (user) {
      await categorizeTransaction(user.uid, txn.id, category);
    }
  }, [user]);

  // Close handlers
  const handleDetailClose = useCallback(() => {
    setDetailSheetOpen(false);
    setTimeout(() => setSelectedTxn(null), 280);
  }, []);

  const handleCatClose = useCallback(() => {
    setCatSheetOpen(false);
    setTimeout(() => setSelectedTxn(null), 280);
  }, []);

  const handleLinkClose = useCallback(() => {
    setLinkSheetOpen(false);
    setTimeout(() => setSelectedTxn(null), 280);
  }, []);

  // Match artwork sheet handlers
  const handleMatchSheetMatch = useCallback(async (transactionId: string, artworkId: string) => {
    if (!user) return;
    try {
      await markVerdictSold(artworkId, matchSheetTxn ? Math.abs(matchSheetTxn.amount) : 0, undefined, undefined, undefined, 'confirmed', transactionId);
      const txnRef = doc(db, 'users', user.uid, 'transactions', transactionId);
      await updateDoc(txnRef, { tpLinkedArtworkId: artworkId });
    } catch (err) {
      console.error('[DashboardPage] Match artwork error:', err);
    }
  }, [user, markVerdictSold, matchSheetTxn]);

  const handleMatchSheetGeneral = useCallback(async (transactionId: string) => {
    if (!user) return;
    try {
      const txnRef = doc(db, 'users', user.uid, 'transactions', transactionId);
      await updateDoc(txnRef, { classification: 'general_art_income' } as any);
    } catch (err) {
      console.error('[DashboardPage] Mark general error:', err);
    }
  }, [user]);

  const handleMatchSheetClose = useCallback(() => {
    setMatchSheetOpen(false);
    setTimeout(() => setMatchSheetTxn(null), 280);
  }, []);

  // Sync handler for SyncRow — sync transactions then recompute earned
  const handleSyncAll = useCallback(async () => {
    if (!user) return;
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('@/firebase/config');
      const syncAll = httpsCallable(functions, 'plaidSyncTransactions');
      await syncAll({});
      // Recompute three-pool earned after sync completes
      const recompute = httpsCallable(functions, 'recomputeEarned');
      recompute({}).catch(console.error);
    } catch (err) {
      console.error('[DashboardPage] Sync error:', err);
    }
  }, [user]);

  // Verdict title map for income linking
  const verdictTitleMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const v of verdicts) {
      map[v.id] = v.inputs.title || `${v.inputs.width}\u00D7${v.inputs.height}"`;
    }
    return map;
  }, [verdicts]);

  // Burn calculations for hero — sourced from useMonthlyFinancials (single source of truth)
  const now = new Date();
  const dayOfMonth = financials.dayOfMonth;
  const daysInMonth = financials.daysInMonth;
  const progressPct = Math.round(financials.progressPct);
  const dayFractionPct = Math.round(burnProgress.dayFraction * 100);
  const monthlyBurn = Math.round(financials.burnTarget / 100);
  const cumulativeDollars = (financials.dailyCumulativeEarned || financials.dailyCumulativeIncome || []).map(c => Math.round(c / 100));
  console.log('[MoneyTab]', { dailyCumulativeIncome: financials.dailyCumulativeIncome, dailyCumulativeEarned: financials.dailyCumulativeEarned, cumulativeDollars, heroEarnedCents: financials.artIncome });

  // Filter transactions to current month so Activity totals match hero earned
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const currentMonthTxns = useMemo(
    () => transactions.filter(t => t.date >= monthStart),
    [transactions, monthStart]
  );

  // Pool 2: sold verdicts awaiting bank payment (current month only)
  const reportedVerdicts = useMemo(() =>
    verdicts.filter(v =>
      v.sold === true &&
      !v.linkedTransactionId &&
      (v.reconciliationStatus === 'reported' || (!v.reconciliationStatus && v.sold)) &&
      v.soldDate && v.soldDate >= monthStart
    ),
  [verdicts, monthStart]);

  // Hero earned from useMonthlyFinancials (single source of truth)
  const heroEarnedCents = financials.artIncome;

  // First name for hero greeting
  const firstName = profile?.displayName ? profile.displayName.split(' ')[0] : '';

  // Build trend arrays for hero dual-layer chart
  const { earnedTrend, spendTrend, burnTrend, currentMonthActualBurn } = useMemo(() => {
    // Earned: extend cumulativeDollars to full month (carry forward today's value)
    const earned = [...cumulativeDollars];
    const lastVal = earned.length > 0 ? earned[earned.length - 1] : 0;
    while (earned.length < daysInMonth) earned.push(lastVal);

    // Build cumulative spend/burn from transactions
    const artSpendBuckets = new Array(daysInMonth).fill(0);
    const allExpenseBuckets: number[] = new Array(daysInMonth).fill(0);
    for (const txn of currentMonthTxns) {
      const dayStr = txn.date?.slice(8, 10);
      const day = parseInt(dayStr, 10);
      if (day < 1 || day > daysInMonth) continue;
      const amt = Math.round(Math.abs(txn.amount) / 100);
      const cat = txn.tpCategory || '';
      if (cat === 'ART_SUPPLY' || cat === 'BUSINESS_EXPENSE') {
        artSpendBuckets[day - 1] += amt;
      }
      if (cat !== 'ART_INCOME' && cat !== 'TEACHING_INCOME') {
        allExpenseBuckets[day - 1] += amt;
      }
    }

    // Convert to cumulative
    const spend: number[] = [];
    const burn: number[] = [];
    let runSpend = 0;
    let runBurn = 0;
    for (let i = 0; i < daysInMonth; i++) {
      runSpend += artSpendBuckets[i];
      runBurn += allExpenseBuckets[i];
      spend.push(runSpend);
      burn.push(runBurn);
    }

    // If burn is all-zero, user set a manual burn target, AND burn source is
    // manual (not Plaid), generate a linear ramp 0 → burnTarget.
    // Plaid users with no transactions get all-zero burn → hero shows / —.
    const isManualBurn = (profile?.burnSource ?? 'manual') === 'manual';
    const burnTargetDollars = Math.round((financials.burnTarget || 0) / 100);
    const burnIsEmpty = burn.every((v: number) => v === 0);
    if (burnIsEmpty && burnTargetDollars > 0 && isManualBurn) {
      for (let i = 0; i < daysInMonth; i++) {
        (burn as number[])[i] = (burnTargetDollars / (daysInMonth - 1)) * i;
      }
    }

    // Current month actual burn total (cumulative spend through today, in dollars)
    const currentMonthActualBurn = burn[Math.min(dayOfMonth - 1, burn.length - 1)] || 0;

    return { earnedTrend: earned, spendTrend: spend, burnTrend: burn, currentMonthActualBurn };
  }, [cumulativeDollars, currentMonthTxns, daysInMonth, financials.burnTarget]);

  return (
    <>
      <style>{PAGE_STYLES}</style>
      <div className="dash-app">
        {/* ── Hero: Two-mode header ── */}
        <div className="dash-enter dash-stagger-1">
          <DashboardHero
            earned={heroEarnedCents}
            spent={financials.artSpend}
            net={financials.artNet}
            burn={hasBankConnected ? currentMonthActualBurn * 100 : financials.burnTarget}
            burnSource={hasBankConnected ? 'plaid' : 'manual'}
            dayOfMonth={dayOfMonth}
            daysInMonth={daysInMonth}
            earnedTrend={earnedTrend}
            spendTrend={spendTrend}
            burnTrend={burnTrend}
            notificationCount={notificationBadgeCount}
            onSettingsPress={() => navigate('/app/settings')}
            onNotificationsPress={() => navigate('/app/notifications')}
            onBurnTap={() => setBurnExplainerOpen(true)}
            mode={heroMode}
            onModeChange={handleHeroModeChange}
            unreviewedSpendCount={profile?.unreviewedSpendCount ?? 0}
            unreviewedEarnedCount={profile?.unreviewedEarnedCount ?? 0}
            onReviewSpendTap={() => {
              const el = document.getElementById('activity-zones');
              if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }}
            onReviewEarnedTap={() => {
              const el = document.getElementById('activity-zones');
              if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }}
          />
        </div>

        {/* ── Body content — overlaps hero gradient by 24px ── */}
        <div style={{
          padding: '0 16px 100px',
          marginTop: -24,
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column' as const,
          gap: 12,
        }}>

          {/* Net summary card — extracted from hero pill in V7 */}
          <NetSummaryCard
            mode={heroMode}
            net={financials.artNet}
            earned={heroEarnedCents}
            burn={hasBankConnected ? currentMonthActualBurn * 100 : financials.burnTarget}
            dayOfMonth={dayOfMonth}
            daysInMonth={daysInMonth}
          />

          {/* Free-tier "Connect your bank" upgrade card */}
          {!isPremium && (
            <div
              className="dash-enter dash-stagger-3"
              onClick={() => setUpgradeSheetOpen(true)}
              style={{
                background: T.white,
                borderRadius: 16,
                padding: '14px 16px',
                marginBottom: 12,
                boxShadow: T.shadowRaised,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: 'pointer',
                fontFamily: T.fontFamily,
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: T.primarySubtle,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="22" x2="21" y2="22" />
                  <line x1="6" y1="18" x2="6" y2="11" />
                  <line x1="10" y1="18" x2="10" y2="11" />
                  <line x1="14" y1="18" x2="14" y2="11" />
                  <line x1="18" y1="18" x2="18" y2="11" />
                  <polygon points="12 2 20 7 4 7" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Connect your bank</div>
                <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 1 }}>
                  See real income & expenses automatically
                </div>
              </div>
              <span style={{
                padding: '4px 10px',
                borderRadius: T.radiusSm,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: T.primary,
                background: T.primarySubtle,
                flexShrink: 0,
              }}>
                PREMIUM
              </span>
            </div>
          )}

          {/* Combined Activity + Accounts widget */}
          <div
            id="activity-zones"
            className="dash-enter dash-stagger-3"
            style={!isPremium ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
          >
            <CombinedAccountsWidget
              transactions={currentMonthTxns}
              mode={heroMode === 'survival' ? 'everything' : 'business'}
              unlimitedTotals={{
                materialsCents: financials.suppliesCents,
                incomeCents: financials.artIncome,
                overheadCents: financials.businessCents,
                personalCents: financials.personalCents,
              }}
              verdictTitleMap={verdictTitleMap}
              animateKey={zoneAnimateKey}
              uncertainTransactions={uncertainTransactions}
              reportedVerdicts={reportedVerdicts}
              matchSuggestions={matchSuggestions}
              onLinkMatch={handleLinkMatch}
              onDismissMatch={(id) => dismissSuggestion(id)}
              reviewSection={(uncertainCount >= 1 || matchSuggestions.length > 0) ? (
                <InlineReviewSection
                  uncertainTransactions={uncertainTransactions}
                  matchSuggestions={matchSuggestions}
                  onCategorize={handleInlineReviewCategorize}
                  onMatchLink={async (s) => {
                    if (!user) return;
                    try {
                      const verdictRef = doc(db, 'users', user.uid, 'verdicts', s.verdictId);
                      await updateDoc(verdictRef, {
                        reconciliationStatus: 'confirmed',
                        linkedTransactionId: s.transactionId,
                      });
                      const txnRef = doc(db, 'users', user.uid, 'transactions', s.transactionId);
                      await updateDoc(txnRef, { tpLinkedArtworkId: s.verdictId });
                      await acceptSuggestion(s.id);
                    } catch (err) {
                      console.error('[DashboardPage] Match link error:', err);
                    }
                  }}
                  onMatchDismiss={(s) => dismissSuggestion(s.id)}
                />
              ) : undefined}
              accounts={plaidAccounts}
              lastSyncAt={accountsSyncAt}
              onViewAll={(category) => navigate(`/app/zone/${category}`)}
              onLinkIncome={(txn) => {
                const formatted = new Date(txn.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                setMatchSheetTxn({ id: txn.id, amount: txn.amount, merchant: txn.merchantName, date: formatted });
                setMatchSheetOpen(true);
              }}
              onTapTransaction={(txn) => {
                setSelectedTxn(txn);
                setDetailSheetOpen(true);
              }}
              onConnectAnother={() => plaidConnect()}
              onSync={handleSyncAll}
            />
          </div>

          {/* Contextual insight card */}
          <InsightCard
            verdicts={verdicts.map(v => ({
              id: v.id,
              medium: v.inputs.medium,
              width: v.inputs.width,
              height: v.inputs.height,
              ttcp: v.calculations.ttcp,
              gutPrice: v.inputs.gutPrice,
              ceiling: v.calculations.ceiling,
              delta: v.calculations.delta,
              effectiveHourly: v.calculations.effectiveHourly,
              sustainabilityStatus: v.flags.sustainabilityStatus,
              sold: v.sold,
              soldPrice: v.soldPrice,
              createdAt: v.createdAt,
            }))}
            floorHourly={profile?.monthlyBurn && profile?.monthlyCapacity ? profile.monthlyBurn / profile.monthlyCapacity : 0}
          />

          {/* Setup checklist — disappears at 5/5 */}
          <CompleteSetupChecklist />

          {/* In-app feedback */}
          <FeedbackRow verdictCount={verdicts.length} />

        </div>
      </div>

      {/* Transaction detail sheet */}
      <TransactionDetailSheet
        transaction={selectedTxn}
        isOpen={detailSheetOpen}
        onClose={handleDetailClose}
        onRecategorize={handleDetailRecategorize}
        onLinkIncome={(txn) => {
          setDetailSheetOpen(false);
          setTimeout(() => {
            const formatted = new Date(txn.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            setMatchSheetTxn({ id: txn.id, amount: txn.amount, merchant: txn.merchantName, date: formatted });
            setMatchSheetOpen(true);
          }, 280);
        }}
        onUnlink={async (txn) => {
          if (!user) return;
          try {
            const txnRef = doc(db, 'users', user.uid, 'transactions', txn.id);
            await updateDoc(txnRef, { tpLinkedArtworkId: null });
            if (txn.tpLinkedArtworkId) {
              const verdictRef = doc(db, 'users', user.uid, 'verdicts', txn.tpLinkedArtworkId);
              await updateDoc(verdictRef, { sold: false, soldPrice: null, soldDate: null, status: 'available', reconciliationStatus: 'none', linkedTransactionId: null });
            }
          } catch (err) {
            console.error('[DashboardPage] Unlink error:', err);
          }
        }}
        verdictTitleMap={verdictTitleMap}
        allTransactions={transactions}
      />

      {/* Categorization sheet */}
      <CategorizationSheet
        transaction={selectedTxn}
        isOpen={catSheetOpen}
        onClose={handleCatClose}
      />

      {/* Income linking sheet */}
      <IncomeLinkingSheet
        transaction={selectedTxn}
        isOpen={linkSheetOpen}
        onClose={handleLinkClose}
        verdicts={verdicts}
        onLink={handleLink}
        onDeclineLink={async (txnId) => {
          if (!user) return;
          const txnRef = doc(db, 'users', user.uid, 'transactions', txnId);
          await updateDoc(txnRef, { tpLinkDeclined: true } as any);
        }}
      />

      {/* Match artwork bottom sheet */}
      {matchSheetTxn && (
        <MatchArtworkBottomSheet
          open={matchSheetOpen}
          onClose={handleMatchSheetClose}
          transaction={matchSheetTxn}
          onMatch={handleMatchSheetMatch}
          onMarkGeneral={handleMatchSheetGeneral}
          verdicts={verdicts}
        />
      )}

      {/* Stat explainer sheets */}
      <StatExplainerSheet
        isOpen={burnExplainerOpen}
        onClose={() => setBurnExplainerOpen(false)}
        title="Monthly burn"
        lines={burnProgress.progressPct >= 100
          ? [EXPLAINERS.BURN_COVERED]
          : [EXPLAINERS.BURN_TEMPLATE]}
      />

      {/* Categorization confirmation toast */}
      {catToast && (
        <div style={{
          position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
          background: '#2D2D2D', color: '#fff', padding: '10px 20px',
          borderRadius: 12, fontSize: 13, fontWeight: 600, zIndex: 400,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'catToastIn 200ms ease-out',
        }}>
          {catToast}
        </div>
      )}
      <style>{`@keyframes catToastIn { from { opacity: 0; transform: translateX(-50%) translateY(8px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
      <UpgradeSheet isOpen={upgradeSheetOpen} onClose={() => setUpgradeSheetOpen(false)} trigger="bank_sync" />

      {/* Plaid connection ceremony */}
      {showConnectionCeremony && connectionResult && createPortal(
        <>
          {/* Scrim */}
          <div
            onClick={dismissCeremony}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(11,12,19,0.35)',
              zIndex: 200,
            }}
          />
          {/* Sheet */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 201,
            background: '#F3EBE3',
            borderRadius: 12,
            padding: 24,
            width: 'calc(100% - 48px)',
            maxWidth: 340,
            textAlign: 'center',
            fontFamily: T.fontFamily,
          }}>
            {/* Checkmark */}
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: T.primarySubtle,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke={T.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            {/* Account name */}
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>
              {connectionResult.institutionName}
              {connectionResult.accountMask && (
                <span style={{ fontSize: 14, fontWeight: 500, color: T.textMuted, marginLeft: 6 }}>
                  ····{connectionResult.accountMask}
                </span>
              )}
            </div>

            {/* Synced count */}
            <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 4 }}>
              {connectionResult.transactionsSynced} transactions synced
            </div>

            {/* Unclassified note */}
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>
              {connectionResult.transactionsSynced > 0
                ? `${connectionResult.transactionsSynced} need review`
                : 'No transactions to review yet'}
            </div>

            {/* Primary CTA */}
            {connectionResult.transactionsSynced > 0 && (
              <button
                onClick={() => {
                  dismissCeremony();
                  // Open the categorization/review flow if available
                }}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: T.radiusMd,
                  border: 'none',
                  background: T.primary,
                  color: T.white,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: T.fontFamily,
                  marginBottom: 10,
                }}
              >
                Review Transactions
              </button>
            )}

            {/* Ghost dismiss */}
            <button
              onClick={dismissCeremony}
              style={{
                width: '100%',
                height: 44,
                borderRadius: T.radiusMd,
                border: 'none',
                background: 'transparent',
                color: T.textSecondary,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: T.fontFamily,
              }}
            >
              Done
            </button>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export default DashboardPage;